import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;
const KEY = "altitude_admin_key";
const STATUS = { retained: "Retenue", to_review: "À vérifier", rejected: "Écartée", expired: "Expirée" };
const eur = (n) => `${Math.round(n).toLocaleString("fr-FR")} €`;
const when = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—");

const client = () => axios.create({ baseURL: API, headers: { "X-Admin-Key": sessionStorage.getItem(KEY) || "" } });

const openHtml = async (path) => {
  const { data } = await client().get(path, { responseType: "text" });
  const w = window.open();
  if (w) { w.document.write(data); w.document.close(); }
};

const Card = ({ title, children, testId }) => (
  <section data-testid={testId} className="rounded-[24px] bg-white p-6 shadow-[0_20px_40px_-30px_rgba(11,21,48,0.5)]">
    <h2 className="font-display text-xl text-navy">{title}</h2>
    <div className="mt-4 text-sm text-navy/80">{children}</div>
  </section>
);

const Login = ({ onDone }) => {
  const [k, setK] = useState("");
  return (
    <form onSubmit={(e) => { e.preventDefault(); sessionStorage.setItem(KEY, k); onDone(); }} className="mx-auto mt-24 max-w-sm space-y-4" data-testid="admin-login-form">
      <h1 className="font-display text-3xl text-navy">Administration</h1>
      <input type="password" value={k} onChange={(e) => setK(e.target.value)} placeholder="Clé d'administration" data-testid="admin-key-input" className="h-12 w-full rounded-2xl border border-navy/15 bg-white px-4" />
      <button type="submit" data-testid="admin-login-button" className="btn-navy w-full justify-center py-3">Entrer</button>
    </form>
  );
};

const Toggle = ({ label, value, onChange, testId }) => (
  <label className="flex items-center justify-between gap-4 py-2">
    <span>{label}</span>
    <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} data-testid={testId} className="h-5 w-5 accent-[#0B1530]" />
  </label>
);

const NumberField = ({ label, value, onSave, testId, step = 1 }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return (
    <label className="flex items-center justify-between gap-4 py-2">
      <span>{label}</span>
      <input type="number" step={step} value={v} onChange={(e) => setV(e.target.value)} onBlur={() => Number(v) !== value && onSave(Number(v))} data-testid={testId} className="h-9 w-24 rounded-xl border border-navy/15 px-2 text-right" />
    </label>
  );
};

const Settings = ({ ov, save }) => {
  const s = ov.settings;
  return (
    <Card title="Réglages et crédits" testId="admin-settings">
      <p data-testid="admin-quota">Crédits SerpApi restants : <strong>{ov.quota?.left ?? "inconnu"}</strong> / {ov.quota?.per_month ?? "?"} par mois · utilisés ce mois-ci par aéroport : {Object.entries(ov.credits_this_month).map(([a, n]) => `${a} ${n}`).join(", ") || "aucun"}</p>
      <p className="mt-1">E-mails : <strong data-testid="admin-email-mode">{ov.email_mode}</strong> · Stripe : {ov.stripe_ready ? "configuré" : "non configuré"} · Webhook : {ov.webhook_ready ? "configuré" : "non configuré"}</p>
      <p className="mt-1">Membres éligibles : {ov.members.eligible} · en attente : {ov.members.pending} · désabonnés des e-mails : {ov.members.unsubscribed}</p>
      <div className="mt-4 divide-y divide-navy/10">
        <Toggle label="Recherche automatique quotidienne" value={s.enabled} onChange={(v) => save({ enabled: v })} testId="admin-toggle-research" />
        <Toggle label="Newsletters automatiques" value={s.newsletter_enabled} onChange={(v) => save({ newsletter_enabled: v })} testId="admin-toggle-newsletter" />
        <Toggle label="Validation manuelle des offres (rien ne part sans mon clic sur Valider)" value={s.manual_validation} onChange={(v) => save({ manual_validation: v })} testId="admin-toggle-manual-validation" />
        <NumberField label="Budget max. par recherche (requêtes)" value={s.budget_per_run} onSave={(v) => save({ budget_per_run: v })} testId="admin-budget-input" />
        <NumberField label="Alerte quota bas (crédits)" value={s.low_quota_alert} onSave={(v) => save({ low_quota_alert: v })} testId="admin-low-quota-input" />
        <NumberField label="Seuil d'économie (0,11 = 11 %)" step={0.01} value={s.saving_threshold} onSave={(v) => save({ saving_threshold: v })} testId="admin-threshold-input" />
        <NumberField label="Newsletters max. par semaine" value={s.max_per_week} onSave={(v) => save({ max_per_week: v })} testId="admin-max-week-input" />
      </div>
    </Card>
  );
};

const OfferRow = ({ o, review, since, p }) => (
  <div className="grid gap-2 border-t border-navy/10 py-3 sm:grid-cols-[1.4fr_0.8fr_1.6fr_auto] sm:gap-4" data-testid={`${p}-offer-${o.key}`}>
    <div><strong>{o.dest_city}</strong>{since && o.first_seen >= since && <span data-testid={`${p}-offer-new-${o.key}`} className="ml-2 rounded-full bg-gold/25 px-2 py-0.5 text-[11px] text-navy">Nouvelle</span>}<br /><span className="whitespace-nowrap">{o.outbound_date} → {o.return_date}</span><br /><span className="text-xs text-navy/50">vérifiée le {when(o.last_verified_at)}</span></div>
    <div className="font-medium">{o.price ? eur(o.price) : "—"}{o.saving_pct > 0 ? ` (−${Math.round(o.saving_pct * 100)} %)` : o.saving_pct < 0 ? ` (+${Math.round(-o.saving_pct * 100)} % vs prix habituel)` : ""}</div>
    <div>{STATUS[o.status]}{o.status === "retained" && (o.approved ? " · validée" : " · non validée")}<br /><span className="text-xs text-navy/60">{(o.reasons || []).join(" · ")}</span></div>
    <div className="flex flex-wrap gap-x-4 gap-y-1 sm:flex-col sm:items-end">
      {o.status === "retained" && !o.approved && (
        <button type="button" onClick={() => review(o.key, "approve")} data-testid={`${p}-validate-${o.key}`} className="text-xs underline">Valider pour envoi</button>
      )}
      {o.status === "to_review" && (
        <>
          <button type="button" onClick={() => review(o.key, "approve", true)} data-testid={`${p}-approve-verified-${o.key}`} className="text-xs underline">Valider (couloir vérifié)</button>
          <button type="button" onClick={() => review(o.key, "approve", false)} data-testid={`${p}-approve-${o.key}`} className="text-xs underline">Valider</button>
          <button type="button" onClick={() => review(o.key, "reject")} data-testid={`${p}-reject-${o.key}`} className="text-xs underline">Écarter</button>
        </>
      )}
      {o.status !== "rejected" && <button type="button" onClick={() => openHtml(`/preview/offers?keys=${encodeURIComponent(o.key)}`)} data-testid={`${p}-preview-${o.key}`} className="text-xs underline">Aperçu</button>}
    </div>
  </div>
);

const OfferTable = ({ rows, review, since, testId, p = "admin" }) => (
  <div data-testid={testId}>{rows.map((o) => <OfferRow key={o.key} o={o} review={review} since={since} p={p} />)}</div>
);

const Offers = ({ offers, review, since }) => (
  <Card title={`Toutes les offres en cours (${offers.length})`} testId="admin-offers">
    {offers.length === 0 ? <p data-testid="admin-offers-empty">Aucune offre pour le moment.</p> : <OfferTable rows={offers} review={review} since={since} />}
  </Card>
);

const RUN_STATUS = { running: "En cours", done: "Terminée", interrupted: "Interrompue", postponed: "Reportée (crédits insuffisants)", error: "Erreur" };

const RunSummary = ({ r, budget }) => {
  if (r.status === "running") {
    const pct = Math.min(100, Math.round(((r.credits_used || 0) / budget) * 100));
    return (
      <div data-testid="admin-research-running" className="rounded-2xl bg-navy p-4 text-ivory">
        <p className="flex items-center gap-2"><span className="h-2.5 w-2.5 animate-pulse rounded-full bg-gold" />Recherche en cours depuis {when(r.started_at)} · {r.credits_used || 0} / {budget} crédits</p>
        <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/15"><div className="h-full bg-gold transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
        <p className="mt-2 text-xs text-ivory/70">Environ 2 minutes. La page se met à jour toute seule, inutile de recharger.</p>
      </div>
    );
  }
  const c = r.counts || {};
  return (
    <div data-testid="admin-research-last" className="rounded-2xl border border-navy/10 bg-ivory/60 p-4">
      <p>Dernière recherche : <strong>{when(r.started_at)}</strong> · <strong data-testid="admin-research-status">{RUN_STATUS[r.status] || r.status}</strong> · {r.credits_used ?? 0} crédits</p>
      {r.counts && <p className="mt-1" data-testid="admin-research-counts">Retenues {c.retained} · À vérifier {c.to_review} · Écartées {c.rejected} · Expirées {c.expired}</p>}
      {r.error && <p className="mt-1 text-red-700">{r.error}</p>}
    </div>
  );
};

const Research = ({ ov, runOffers, review, reload }) => {
  const last = ov.research_runs[0];
  const running = last?.status === "running";
  const start = async () => {
    if (!window.confirm(`Lancer une recherche maintenant ? Elle consomme jusqu'à ${ov.settings.budget_per_run} crédits SerpApi.`)) return;
    try { await client().post("/research/run"); toast.success("Recherche lancée"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec du lancement"); }
    setTimeout(reload, 800);
  };
  return (
    <Card title="Recherche d'offres" testId="admin-research">
      <button type="button" onClick={start} disabled={running} data-testid="admin-run-research" className="btn-navy px-4 py-2 text-sm disabled:opacity-50">{running ? "Recherche en cours…" : "Lancer une recherche"}</button>
      <div className="mt-4">{last ? <RunSummary r={last} budget={ov.settings.budget_per_run} /> : <p>Aucune recherche lancée pour le moment.</p>}</div>
      {last && !running && (
        <div className="mt-5">
          <h3 className="font-display text-base text-navy">Offres analysées lors de cette recherche ({runOffers.length})</h3>
          {runOffers.length === 0 ? <p className="mt-2" data-testid="admin-run-offers-empty">Aucune nouvelle destination analysée : toutes les pistes trouvées étaient déjà connues ou hors critères.</p>
            : <OfferTable rows={runOffers} review={review} since={last.started_at} testId="admin-run-offers" p="run" />}
        </div>
      )}
      {ov.research_runs.length > 1 && (
        <details className="mt-5" data-testid="admin-research-history">
          <summary className="cursor-pointer text-navy/70">Recherches précédentes</summary>
          {ov.research_runs.slice(1).map((r) => <p key={r.id} className="mt-1">{when(r.started_at)} · {RUN_STATUS[r.status] || r.status} · {r.credits_used ?? 0} crédits{r.counts ? ` · ${r.counts.retained} retenue(s), ${r.counts.to_review} à vérifier` : ""}</p>)}
        </details>
      )}
    </Card>
  );
};

const Runs = ({ ov }) => (
  <Card title="Historique des newsletters" testId="admin-runs">
    {ov.newsletter_runs.map((r) => <p key={r.id}>{when(r.created_at)} · {r.airport} · {r.status} · {r.sent} envoi(s){r.reason ? ` · ${r.reason}` : ""}</p>)}
    {!ov.newsletter_runs.length && <p>Aucune newsletter envoyée.</p>}
  </Card>
);

const Actions = () => (
    <Card title="Aperçus des e-mails" testId="admin-actions">
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={() => openHtml("/preview/offers")} data-testid="admin-preview-sample" className="rounded-full border border-navy/20 px-4 py-2 text-sm">Aperçu e-mail d'offres</button>
        <button type="button" onClick={() => openHtml("/preview/payment")} data-testid="admin-preview-payment" className="rounded-full border border-navy/20 px-4 py-2 text-sm">Aperçu e-mail de paiement</button>
      </div>
    </Card>
);

const StripeSetup = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { client().get("/stripe").then((r) => setSt(r.data)).catch(() => {}); }, []);
  const run = async () => {
    if (!window.confirm("Créer ou mettre à jour l'offre, le webhook et l'espace de gestion dans le compte Stripe de cette clé ? Aucun paiement n'est effectué.")) return;
    setBusy(true);
    try { const { data } = await client().post("/stripe/setup"); setSt(data); toast.success("Stripe configuré"); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
    setBusy(false);
  };
  return (
    <Card title="Stripe" testId="admin-stripe">
      {st && (
        <p data-testid="admin-stripe-status" className="break-all">Mode : <strong>{st.mode === "live" ? "RÉEL" : "test"}</strong> · {st.configured ? `configuré (${st.price_id})` : "non configuré automatiquement"}
          {st.configured && <> · webhook : {st.webhook_url}{!st.url_matches && " — adresse du site changée, relancez la configuration"}</>}</p>
      )}
      <button type="button" onClick={run} disabled={busy} data-testid="admin-stripe-setup" className="btn-navy mt-3 px-4 py-2 text-sm disabled:opacity-60">{busy ? "Configuration…" : "Configurer Stripe"}</button>
    </Card>
  );
};

const STATUS_M = { pending_payment: "En attente de paiement", processing: "En cours", trial: "Essai", trial_cancelling: "Essai, résiliation programmée", active: "Actif", active_cancelling: "Actif, résiliation programmée", past_due: "Paiement échoué", cancelled: "Résilié" };
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

const Subscribers = ({ rows }) => (
  <Card title={`Abonnés (${rows.filter((r) => r.eligible).length} éligibles sur ${rows.length} inscrits)`} testId="admin-subscribers">
    {rows.length === 0 ? <p data-testid="admin-subscribers-empty">Aucun inscrit pour le moment.</p> : (
      <div className="overflow-x-auto"><table className="w-full text-left text-xs sm:text-sm">
        <thead><tr className="text-navy/50"><th className="py-2 pr-3">Abonné</th><th className="pr-3">Aéroport</th><th className="pr-3">Statut</th><th className="pr-3">Fin d'essai / droits</th><th>Offres</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.id} className="border-t border-navy/10" data-testid={`admin-subscriber-${r.id}`}>
            <td className="py-2 pr-3">{r.name && <><strong>{r.name}</strong><br /></>}{r.email || r.stripe_email}{r.stripe_email && r.email && r.stripe_email !== r.email ? <><br /><span className="text-navy/50">Stripe : {r.stripe_email}</span></> : null}</td>
            <td className="pr-3">{r.airport}</td>
            <td className="pr-3">{STATUS_M[r.status] || r.status}</td>
            <td className="pr-3">{r.access_until ? `fin le ${day(r.access_until)}` : r.status?.startsWith("trial") ? day(r.trial_end) : r.status === "active" ? `renouv. ${day(r.current_period_end)}` : "—"}</td>
            <td>{r.unsubscribed ? "Désabonné" : r.eligible ? "Reçoit les offres" : "Ne reçoit pas"}</td>
          </tr>))}</tbody>
      </table></div>
    )}
  </Card>
);

const SendPanel = ({ draft, reload }) => {
  const [keys, setKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const toggle = (k) => setKeys((v) => (v.includes(k) ? v.filter((x) => x !== k) : v.length < 4 ? [...v, k] : v));
  const send = async () => {
    if (!window.confirm(`Envoyer ${keys.length} offre(s) à ${draft.recipients.length} abonné(s) ? Chaque offre est revérifiée avant envoi (jusqu'à 3 crédits par offre).`)) return;
    setBusy(true);
    try {
      const { data } = await client().post("/newsletter/send", { airport: "CDG", keys });
      toast[data.status === "done" ? "success" : "error"](data.status === "done" ? `${data.sent} e-mail(s) envoyé(s)` : data.reason || "Envoi impossible");
      setKeys([]);
      reload();
    } finally { setBusy(false); }
  };
  return (
    <Card title="Envoyer une newsletter" testId="admin-send-panel">
      <p>Envois cette semaine : {draft.sent_this_week} / {draft.max_per_week}{draft.gap_ok ? "" : " · dernier envoi il y a moins de 2 jours"}</p>
      {draft.offers.length === 0 ? <p className="mt-3" data-testid="admin-send-empty">{draft.manual_validation ? "Aucune offre validée : validez d'abord une offre retenue ci-dessous." : "Aucune offre retenue disponible."}</p> : (
        <div className="mt-3 space-y-2">{draft.offers.map((o) => (
          <label key={o.key} className="flex items-start gap-3" data-testid={`admin-send-offer-${o.key}`}>
            <input type="checkbox" disabled={o.new_for === 0} checked={keys.includes(o.key)} onChange={() => toggle(o.key)} className="mt-1 h-4 w-4 accent-[#0B1530] disabled:opacity-40" data-testid={`admin-send-check-${o.key}`} />
            <span><strong>{o.dest_city}</strong> · {o.outbound_date} → {o.return_date} · {eur(o.price)} (−{Math.round(o.saving_pct * 100)} %) · nouvelle pour {o.new_for} abonné(s)</span>
          </label>))}</div>
      )}
      <p className="mt-4" data-testid="admin-send-recipients"><strong>Destinataires ({draft.recipients.length}) :</strong> {draft.recipients.map((r) => r.email).join(", ") || "aucun abonné éligible"}</p>
      <div className="mt-4 flex flex-wrap gap-3">
        <button type="button" disabled={!keys.length} onClick={() => openHtml(`/preview/offers?keys=${encodeURIComponent(keys.join(","))}`)} data-testid="admin-send-preview" className="rounded-full border border-navy/20 px-4 py-2 text-sm disabled:opacity-40">Aperçu de l'e-mail</button>
        <button type="button" disabled={!keys.length || !draft.recipients.length || busy} onClick={send} data-testid="admin-send-button" className="btn-navy px-4 py-2 text-sm disabled:opacity-40">{busy ? "Envoi…" : "Envoyer"}</button>
      </div>
    </Card>
  );
};

export default function Admin() {
  const [ov, setOv] = useState(null);
  const [offers, setOffers] = useState([]);
  const [draft, setDraft] = useState(null);
  const [subs, setSubs] = useState([]);
  const [runOffers, setRunOffers] = useState([]);
  const [auth, setAuth] = useState(!!sessionStorage.getItem(KEY));

  const load = useCallback(async () => {
    try {
      const c = client();
      const [a, b, d, s] = await Promise.all([c.get("/overview"), c.get("/offers"), c.get("/newsletter/draft"), c.get("/subscribers")]);
      setOv(a.data);
      setOffers(b.data);
      setDraft(d.data);
      setSubs(s.data);
      const last = a.data.research_runs[0];
      if (last && last.status !== "running") setRunOffers((await c.get(`/research/runs/${last.id}/offers`)).data);
    } catch (e) {
      if (e.response?.status === 401) { sessionStorage.removeItem(KEY); setAuth(false); toast.error("Clé incorrecte"); }
    }
  }, []);

  useEffect(() => { if (auth) load(); }, [auth, load]);

  const running = ov?.research_runs[0]?.status === "running";
  useEffect(() => {
    if (!running) { return undefined; }
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [running, load]);

  if (!auth) return <div className="min-h-screen bg-ivory px-4"><Login onDone={() => setAuth(true)} /></div>;
  if (!ov) return <div className="min-h-screen bg-ivory" data-testid="admin-loading" />;

  const save = async (patch) => { await client().put("/settings", patch); toast.success("Enregistré"); load(); };
  const review = async (key, decision, aisle = false) => { await client().post(`/offers/${encodeURIComponent(key)}/review`, { decision, aisle_verified: aisle }); load(); };

  return (
    <div className="min-h-screen bg-ivory px-4 py-10 sm:px-8" data-testid="admin-page">
      <div className="mx-auto max-w-4xl space-y-5">
        <h1 className="font-display text-4xl text-navy">Administration</h1>
        <Settings ov={ov} save={save} />
        <Research ov={ov} runOffers={runOffers} review={review} reload={load} />
        <Offers offers={offers} review={review} since={ov.research_runs[0]?.started_at} />
        {draft && <SendPanel draft={draft} reload={load} />}
        <Subscribers rows={subs} />
        <Actions />
        <StripeSetup />
        <Runs ov={ov} />
      </div>
    </div>
  );
}
